// (c) chaehongee. Confidential and proprietary.

import { useState, useRef, useEffect } from 'react'
import { useAuth } from '../../store/hooks/useAuth'
import { getUserProfile } from '../../services/user'
import type { UserResponse } from 'models-ts/user'
import styles from './UserMenu.module.css'
import { LogOut, Settings, Sun, Moon } from 'lucide-react'
import { useTheme } from '../../hooks/useTheme'

export function UserMenu() {
    const { user, signOut } = useAuth()
    const { theme, setTheme } = useTheme()
    const [isOpen, setIsOpen] = useState(false)
    const [profile, setProfile] = useState<UserResponse | null>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (user) {
            loadProfile()
        }
    }, [user])

    const loadProfile = async () => {
        try {
            const data = await getUserProfile()
            setProfile(data)
        } catch (err) {
            console.error('Failed to load profile:', err)
        }
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    const handleSignOut = async () => {
        try {
            await signOut()
            setIsOpen(false)
        } catch (error) {
            console.error('Sign out failed:', error)
        }
    }

    const handleNavigateToDashboard = () => {
        window.location.href = '/dashboard'
        setIsOpen(false)
    }

    const handleNavigateToBilling = () => {
        window.location.href = '/dashboard?tab=billing'
        setIsOpen(false)
    }

    if (!user) return null

    const displayName = profile?.display_name || user.email || 'User'
    const userInitial = displayName.charAt(0).toUpperCase()
    const creditBalance = profile?.credit_balance
    const creditsLabel =
        typeof creditBalance === 'number'
            ? `${new Intl.NumberFormat().format(creditBalance)} cr`
            : null
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)

    return (
        <div className={styles.userMenuContainer} ref={menuRef}>
            {creditsLabel && (
                <button
                    type="button"
                    className={styles.creditsPill}
                    aria-label={`Credits: ${creditsLabel}. View billing.`}
                    onClick={handleNavigateToBilling}
                >
                    {creditsLabel}
                </button>
            )}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={styles.userAvatar}
                aria-label="User menu"
                aria-expanded={isOpen}
            >
                {userInitial}
            </button>

            {isOpen && (
                <div className={styles.dropdown}>
                    <button
                        type="button"
                        className={styles.userInfo}
                        onClick={handleNavigateToDashboard}
                    >
                        <div className={styles.userName}>{displayName}</div>
                        {profile?.display_name && user.email && (
                            <div className={styles.userEmail}>{user.email}</div>
                        )}
                    </button>
                    <div className={styles.divider} />
                    <button
                        type="button"
                        className={styles.menuItem}
                        onClick={() => setTheme(isDark ? 'light' : 'dark')}
                        aria-label="Toggle theme"
                    >
                        {isDark ? <Sun className={styles.menuIcon} /> : <Moon className={styles.menuIcon} />}
                        <span>{isDark ? 'Light mode' : 'Dark mode'}</span>
                    </button>
                    <div className={styles.divider} />
                    <button onClick={handleNavigateToDashboard} className={styles.menuItem}>
                        <Settings className={styles.menuIcon} />
                        <span>Settings</span>
                    </button>
                    <button onClick={handleSignOut} className={styles.menuItem}>
                        <LogOut className={styles.menuIcon} />
                        <span>Log out</span>
                    </button>
                </div>
            )}
        </div>
    )
}

